import { useState } from "react";
import FocusLock from "react-focus-lock";
import { Controller, useForm } from "react-hook-form";
import { LockIcon } from "@/shared/icons";
import { Button, TextInput } from "@/shared/ui";
import AuthTitle from "@/domains/auth/components/AuthTitle.tsx";

interface AuthResetPasswordUpdateForm {
  password: string;
  passwordRepeat: string;
}

interface AuthResetPasswordUpdateProps {
  onSubmit: (data: AuthResetPasswordUpdateForm) => void;
  error?: string;
  pending: boolean;
}

function AuthUpdatePassword({ onSubmit, error = "", pending }: AuthResetPasswordUpdateProps) {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<AuthResetPasswordUpdateForm>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      password: "",
      passwordRepeat: "",
    },
  });
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  return (
    <>
      <AuthTitle>Создайте новый пароль</AuthTitle>
      <FocusLock className="contents">
        <form
          noValidate
          tabIndex={0}
          onSubmit={handleSubmit((data) => {
            if (!errors.password && !errors.passwordRepeat) {
              onSubmit(data);
            }
          })}
          className="gap-3 grow flex flex-col outline-none"
        >
          <Controller
            name="password"
            control={control}
            rules={{
              required: "Пароль не указан",
              minLength: {
                value: 6,
                message: "Минимальная длина пароля 6 символов",
              },
              maxLength: {
                value: 32,
                message: "Максимальная длина пароля 32 символа",
              },
            }}
            render={({ field }) => (
              <TextInput
                {...field}
                type="password"
                placeholder="Придумайте пароль"
                title="Пароль:"
                icon={<LockIcon size={16} />}
                required
                inputClassName="text-center"
                invalid={errors.password?.message || ""}
                passwordVisible={isPasswordVisible}
                onChangePasswordVisible={setIsPasswordVisible}
              />
            )}
          />

          <Controller
            name="passwordRepeat"
            control={control}
            rules={{
              required: "Повторите пароль",
              validate: (value) => value === watch("password") || "Пароли не совпадают",
            }}
            render={({ field }) => (
              <TextInput
                {...field}
                type="password"
                placeholder="Повторите новый пароль"
                title="Повторите новый пароль:"
                hiddenTitle
                icon={<LockIcon size={16} />}
                required
                inputClassName="text-center"
                invalid={errors.passwordRepeat?.message || ""}
                passwordVisible={isPasswordVisible}
                onChangePasswordVisible={setIsPasswordVisible}
              />
            )}
          />

          <div className="mt-auto grid gap-2.5">
            {!!error && <div className="alert alert--danger">*{error}</div>}
            <Button submit className="btn--md w-full" loading={pending}>
              Готово
            </Button>
          </div>
        </form>
      </FocusLock>
    </>
  );
}

export default AuthUpdatePassword;
